// src/components/explorers/weatherDataExplorer/WeatherProductsPanel.tsx
// Weather products browser — shows CMS weather files for a given date with prev/next navigation

import React, { useState, useEffect } from 'react';
import { getCmsUrl, getCmsApiUrl } from '../../../services/configService';
import { proxyCmsUrl } from '../../../services/cmsFileProxy';
import AppModal from '../../common/AppModal';
import MultiFileUploader from '../../upload/MultiFileUploader';
import { usePermissions } from '../../../context/PermissionContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft, faChevronRight, faCloudSunRain, faCloudSun,
  faFire, faFireFlameCurved, faBolt, faClock, faMap,
  faFileLines, faTriangleExclamation, faChartArea, faUpload,
  faLink,
} from '@fortawesome/free-solid-svg-icons';
import { faYoutube } from '@fortawesome/free-brands-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface WeatherProductsPanelProps {
  onClose: () => void;
}

interface WeatherFileCard {
  id: string;
  heading: string;
  date: string | null;
  region: string | null;
  fireId: string | null;
  description: string | null;
  fileUrl: string | null;
  nodeUrl: string;
  kind: 'file' | 'video' | 'link';
}

// Map weather subcategory names to FA icons
const subcategoryIcon = (name: string): { icon: IconDefinition; color: string } => {
  const lower = name.toLowerCase();
  if (lower.includes('fire situation'))      return { icon: faFire, color: '#c62828' };
  if (lower.includes('fire spot') || lower.includes('spot forecast'))
                                             return { icon: faFireFlameCurved, color: '#d84315' };
  if (lower.includes('lightning'))           return { icon: faBolt, color: '#f9a825' };
  if (lower.includes('rain'))               return { icon: faCloudSunRain, color: '#1565c0' };
  if (lower.match(/\d{4}$|0900|1300|1700/)) return { icon: faClock, color: '#546e7a' };
  if (lower.includes('forecast map'))        return { icon: faMap, color: '#2e7d32' };
  if (lower.includes('advisory'))            return { icon: faTriangleExclamation, color: '#e65100' };
  if (lower.includes('chart') || lower.includes('surface'))
                                             return { icon: faChartArea, color: '#6a1b9a' };
  if (lower.includes('forecast'))            return { icon: faFileLines, color: '#37474f' };
  return { icon: faCloudSun, color: '#1976d2' };
};

const stripHtml = (html: string): string =>
  html.replace(/<[^>]*>/g, '').trim();

const formatDisplayDate = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-');
  const d = new Date(+year, +month - 1, +day);
  return d.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

const shiftDate = (dateStr: string, days: number): string => {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

// "Today" in NWT, not UTC — toISOString() rolls over at 18:00 NWT (MDT) and the
// panel would open on tomorrow's date for evening users.
const todayStr = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Yellowknife' }).format(new Date());

const WeatherProductsPanel: React.FC<WeatherProductsPanelProps> = ({ onClose }) => {
  const [date, setDate] = useState(todayStr());
  const [cards, setCards] = useState<WeatherFileCard[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isUploaderOpen, setIsUploaderOpen] = useState(false);
  const { canDo } = usePermissions();
  const canUpload = canDo('weather', 'upload');
  const API_URL = getCmsApiUrl();
  const CMS_URL = getCmsUrl();

  // Auto-refresh every 15s so users waiting for new files see them appear
  useEffect(() => {
    const interval = setInterval(() => setRefreshKey(k => k + 1), 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!CMS_URL) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          'filter[field_file_date]': date,
          'include': 'field_weather_subcategory,field_file,field_region,field_wildfire_ref',
          'sort': '-created',
          'page[limit]': '20',
        });

        const res = await fetch(
          `${API_URL}/jsonapi/node/weather_file?${params}`,
          { headers: { 'Accept': 'application/vnd.api+json' }, credentials: 'include' as RequestCredentials },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const included = data.included || [];
        const totalCount = data.meta?.count ?? (data.data || []).length;

        const results: WeatherFileCard[] = (data.data || []).map((node: any) => {
          // Resolve subcategory
          const subRef = node.relationships?.field_weather_subcategory?.data;
          const subTerm = subRef
            ? included.find((i: any) => i.id === subRef.id && i.type === subRef.type)
            : null;

          // Resolve region
          const regRef = node.relationships?.field_region?.data;
          const regTerm = regRef
            ? included.find((i: any) => i.id === regRef.id && i.type === regRef.type)
            : null;

          // Resolve fire (wildfire taxonomy ref)
          const fireRef = node.relationships?.field_wildfire_ref?.data;
          const fireTerm = fireRef
            ? included.find((i: any) => i.id === fireRef.id && i.type === fireRef.type)
            : null;

          // Resolve file
          const fileRef = node.relationships?.field_file?.data;
          const fileEntity = fileRef
            ? included.find((i: any) => i.id === fileRef.id && i.type === fileRef.type)
            : null;

          const bodyRaw = node.attributes.body?.value || node.attributes.body?.processed || '';

          // Three modes (per WEATHER_VIDEO_INTEGRATION doc): cascade
          // youtube_video_id → video_url → file.
          const youtubeId = node.attributes.field_youtube_video_id;
          const videoUrl = node.attributes.field_video_url?.uri;
          const kind: 'video' | 'link' | 'file' =
            youtubeId ? 'video' : videoUrl ? 'link' : 'file';

          return {
            id: node.id,
            heading: subTerm?.attributes?.name || node.attributes.title,
            date: node.attributes.field_file_date,
            region: regTerm?.attributes?.name || null,
            fireId: fireTerm?.attributes?.name || null,
            description: bodyRaw ? stripHtml(bodyRaw) : null,
            fileUrl: proxyCmsUrl(fileEntity?.attributes?.uri?.url ?? null),
            nodeUrl: `${CMS_URL}/node/${node.attributes.drupal_internal__nid}`,
            kind,
          };
        });

        if (!cancelled) {
          setCards(results);
          setTotal(totalCount);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setCards([]);
          setTotal(0);
          setLoading(false);
          setError(err instanceof Error ? err.message : 'Failed to load weather files');
        }
      }
    })();

    return () => { cancelled = true; };
  }, [CMS_URL, API_URL, date, refreshKey]);

  const isToday = date === todayStr();
  const viewAllUrl = CMS_URL ? `${CMS_URL}/files?type=weather_file` : null;

  return (
    <AppModal isOpen={true} onClose={onClose} width={480} maxHeight="80vh">
      <div className="weather-products-panel">
        <div className="wp-header">
          <div className="wp-header__left">
            <FontAwesomeIcon icon={faCloudSunRain} className="wp-header__icon" />
            <h3>Weather Products</h3>
          </div>
          <div className="wp-header__actions">
            {canUpload && (
              <button
                type="button"
                className="btn btn-sm btn-secondary wp-header__upload"
                onClick={() => setIsUploaderOpen(true)}
                title="Upload weather files"
              >
                <FontAwesomeIcon icon={faUpload} /> Upload
              </button>
            )}
            {viewAllUrl && canDo('cms', 'read') && (
              <a
                className="btn btn-sm btn-primary wp-header__explore"
                href={viewAllUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Explore all
              </a>
            )}
          </div>
        </div>

        <div className="wp-date-nav">
          <button
            type="button"
            className="wp-date-nav__btn"
            onClick={() => setDate(d => shiftDate(d, -1))}
            title="Previous day"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <span className="wp-date-nav__label">{formatDisplayDate(date)}</span>
          <button
            type="button"
            className="wp-date-nav__btn"
            onClick={() => setDate(d => shiftDate(d, 1))}
            disabled={isToday}
            title="Next day"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>

        {!loading && total > 0 && (
          <div className="wp-file-count">{total} file{total !== 1 ? 's' : ''}</div>
        )}

        <div className="wp-body">
          {loading && <p className="wp-muted">Loading...</p>}
          {error && <p className="wp-error">Error: {error}</p>}
          {!loading && !error && cards.length === 0 && (
            <p className="wp-muted">No weather files for {formatDisplayDate(date)}</p>
          )}

          {cards.length > 0 && (
            <div className="wp-card-list">
              {cards.map(card => {
                const { icon, color } =
                  card.kind === 'video' ? { icon: faYoutube, color: '#c4302b' }
                  : card.kind === 'link' ? { icon: faLink, color: '#0d6efd' }
                  : subcategoryIcon(card.heading);
                return (
                  <a
                    key={card.id}
                    className="wp-card"
                    href={card.nodeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="wp-card__icon" style={{ backgroundColor: color }}>
                      <FontAwesomeIcon icon={icon} />
                    </div>
                    <div className="wp-card__body">
                      <h4 className="wp-card__heading">{card.heading}</h4>
                      {card.region && (
                        <span className="wp-card__region">{card.region}</span>
                      )}
                      {(card.fireId || card.description) && (
                        <p className="wp-card__desc">
                          {(() => {
                            const truncatedDesc = card.description && card.description.length > 200
                              ? card.description.slice(0, 200) + '...'
                              : card.description;
                            if (card.fireId && truncatedDesc) return `${card.fireId} - ${truncatedDesc}`;
                            if (card.fireId) return card.fireId;
                            return truncatedDesc;
                          })()}
                        </p>
                      )}
                    </div>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <MultiFileUploader
        contentType="weather_file"
        context={{}}
        isOpen={isUploaderOpen}
        onClose={() => setIsUploaderOpen(false)}
        onUploadComplete={() => {
          setIsUploaderOpen(false);
          setRefreshKey((k) => k + 1);
        }}
      />
    </AppModal>
  );
};

export default WeatherProductsPanel;
